import type { ReactNode } from 'react'

import { cn } from '@/lib/ui/cn'

interface EmptyStateProps {
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
  /** Smaller medallion and padding, for use inside a panel. */
  compact?: boolean
}

/**
 * A quiet place for nothing-yet and could-not-load: the icon in a ringed
 * medallion, a title, a line of context and the one action that helps.
 */
export function EmptyState({ icon, title, description, action, className, compact = false }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center text-center', compact ? 'px-4 py-7' : 'px-6 py-12 sm:py-16', className)}>
      {icon ? (
        <span
          aria-hidden="true"
          className={cn(
            'relative mb-4 flex items-center justify-center rounded-full text-[color:var(--bl-gold-text)]',
            'bg-[radial-gradient(circle_at_50%_35%,rgb(var(--color-primary)/0.22),transparent_70%)] ring-1 ring-[rgb(var(--color-primary)/0.3)]',
            'shadow-[0_0_0_6px_rgb(var(--color-primary)/0.05),0_0_40px_-8px_var(--bl-glow)]',
            compact ? 'h-12 w-12' : 'h-16 w-16',
          )}
        >
          {icon}
        </span>
      ) : null}
      <p className={cn('font-semibold text-[color:var(--bl-ink)]', compact ? 'text-sm' : 'text-base')}>{title}</p>
      {description ? <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-[color:var(--bl-ink-2)]">{description}</p> : null}
      {action ? <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{action}</div> : null}
    </div>
  )
}
